import React from "react";
import { View } from "react-native";
import Placeholder from "../../general/Placeholder/Placeholder";
import AiTutorCardProgress from "../AiTutorCardProgress/AiTutorCardProgress";
import AxioResourceCard from "../../customCoursework/AxioResourceCard/AxioResourceCard";
import { MotiView } from "moti";
import { Link, FolderUser, ArrowsClockwise, Sparkle, FileText, HeadCircuit } from "@phosphor-icons/react";
import AssessmentCard from "../../customCoursework/AssessmentCard/AssessmentCard";
import { RouteProp, useNavigation, useRoute } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { useAppConfig } from "../../../AppConfigProvider";
import { ActivityState, AssessmentSubmission, File } from "../../../graphql/generated/graphql";
import { RootStackParamList } from "../../../navigation/AppNavigator.web";
import { ResourceType } from "../../../contexts/ActiveCourseContext";

type ResourceDetails = {
  title?: string;
  description?: string | null;
  videoUrl?: string | null;
  isPending?: boolean;
  isComplete?: boolean;
  grade?: number | null;
  totalObjectives?: number | null;
  completedObjectives?: number | null;
};

type ResourceItem = {
  id: string;
  resourceType: ResourceType;
  title?: string | null;
  details?: ResourceDetails;
  files?: File[];
  isHovered?: boolean;
  dueDate?: string | Date | null;
  activityState?: ActivityState | null;
  scheduledDate?: string | Date | null;
  latestAssessmentSubmission?: AssessmentSubmission | null;
  aiTutorIdOfReview?: string | null;
  isReviewContentBeingGenerated?: boolean;
};

interface AiTutorCardProgressListProps {
  items?: ResourceItem[];
  onPress?: (id: string, aiTutorIdOfReview?: string | null, assessmentSubmissionId?: string | null) => void;
  onHoverIn?: (id: string) => void;
  onHoverOut?: (id: string) => void;
  isInitialLoad?: boolean;
  isPreviewMode?: boolean;
  onResetILE?: (aiTutorId: string) => void;
  resettingILEId?: string | null;
}

function AiTutorCardProgressList({
  items = [],
  onPress = () => {},
  onHoverIn = () => {},
  onHoverOut = () => {},
  isInitialLoad = false,
  isPreviewMode = false,
  onResetILE,
  resettingILEId = null,
}: AiTutorCardProgressListProps) {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const route = useRoute<RouteProp<RootStackParamList, "Custom Module Student">>();

  const { primaryColor } = useAppConfig();

  const { course_id: courseId, integration_id: integrationId, module_id: moduleId } = route.params;

  const resourceTypeToIcon: Partial<Record<ResourceType, JSX.Element>> = {
    [ResourceType.ACTIVITY_RESOURCE]: <Link width={24} height={24} fill={primaryColor} />,
    [ResourceType.ACTIVITY_INTERACTIVE_LEARNING_EXPERIENCE]: <Sparkle width={24} height={24} fill={primaryColor} />,
    [ResourceType.ACTIVITY_ASSIGNMENT]: <FileText width={24} height={24} fill={primaryColor} />,
    [ResourceType.ACTIVITY_REVIEW]: <ArrowsClockwise size={24} color={primaryColor} />,
    [ResourceType.ACTIVITY_PROJECT]: <FolderUser width={24} height={24} fill={primaryColor} />,
    [ResourceType.ACTIVITY_ASSESSMENT]: <HeadCircuit width={24} height={24} fill={primaryColor} />,
  };

  const handleAssessmentButtonPress = (id: string) => {
    navigation.navigate("Custom Course Assessment", {
      assessment_id: id,
      course_id: courseId,
      integration_id: integrationId,
      module_id: moduleId,
    });
  };

  const renderResourceCard = (item: ResourceItem, index: number) => {
    const icon = resourceTypeToIcon[item.resourceType];

    const animationConfig = isInitialLoad
      ? {
          from: {
            opacity: 0,
            translateY: 20,
            scale: 0.95,
          },
          animate: {
            opacity: 1,
            translateY: 0,
            scale: 1,
          },
          delay: 300 + index * 300,
          transition: { type: "timing", duration: 800 },
        }
      : {
          from: {
            opacity: 1,
            translateY: 0,
            scale: 1,
          },
          animate: {
            opacity: 1,
            translateY: 0,
            scale: 1,
          },
          delay: 0,
          transition: { type: "timing", duration: 0 },
        };

    if (
      item.resourceType === ResourceType.ACTIVITY_RESOURCE ||
      item.resourceType === ResourceType.ACTIVITY_ASSIGNMENT ||
      item.resourceType === ResourceType.ACTIVITY_PROJECT
    ) {
      return (
        <MotiView key={item.id} {...animationConfig}>
          <AxioResourceCard
            title={item.details?.title}
            description={item.details?.description}
            videoUrl={item.details?.videoUrl}
            icon={icon}
            files={item.files || []}
            isHovered={item.isHovered}
            onPress={() => onPress(item.id)}
            onHoverIn={() => onHoverIn(item.id)}
            onHoverOut={() => onHoverOut(item.id)}
            dueDate={item.dueDate}
            activityState={item.activityState}
            scheduledDate={item.scheduledDate}
            isPending={item.details?.isPending}
            isComplete={item.details?.isComplete}
            grade={item.details?.grade}
          />
        </MotiView>
      );
    }

    if (item.resourceType === ResourceType.ACTIVITY_ASSESSMENT) {
      return (
        <MotiView key={item.id} {...animationConfig}>
          <AssessmentCard
            onButtonPress={() => handleAssessmentButtonPress(item.id)}
            grade={item.latestAssessmentSubmission?.totalPoints}
            status={item.latestAssessmentSubmission?.status}
            isEvaluationPending={item.latestAssessmentSubmission?.isEvaluationPending}
            onPress={() => onPress(item?.id, null, item?.latestAssessmentSubmission?.id)}
            onHoverIn={() => onHoverIn(item.id)}
            onHoverOut={() => onHoverOut(item.id)}
            isHovered={item.isHovered}
            maximumPoints={item.latestAssessmentSubmission?.maximumPoints}
            activityState={item.activityState}
            scheduledDate={item.scheduledDate}
            dueDate={item.dueDate}
          />
        </MotiView>
      );
    }

    // Handle both aiTutor and review types with AiTutorCardProgress
    // For ILEs, use item.id; for reviews, use aiTutorIdOfReview
    const isILE = item.resourceType === ResourceType.ACTIVITY_INTERACTIVE_LEARNING_EXPERIENCE;
    const isReview = item.resourceType === ResourceType.ACTIVITY_REVIEW;
    const aiTutorId = isILE ? item.id : (item.aiTutorIdOfReview ?? null);
    const showResetButton = isPreviewMode && (isILE || isReview) && onResetILE && aiTutorId;

    return (
      <MotiView key={item.id} {...animationConfig}>
        <AiTutorCardProgress
          title={item.title}
          isHovered={item.isHovered}
          isComplete={item.details?.isComplete}
          totalObjectives={item.details?.totalObjectives}
          icon={icon}
          completedObjectives={item.details?.completedObjectives}
          dueDate={item.dueDate}
          onPress={() => onPress(item.id, item.aiTutorIdOfReview)} // Pass aiTutorId for reviews
          onHoverIn={() => onHoverIn(item.id)}
          onHoverOut={() => onHoverOut(item.id)}
          isReviewContentBeingGenerated={item.isReviewContentBeingGenerated}
          activityState={item.activityState}
          scheduledDate={item.scheduledDate}
          isPreviewMode={showResetButton}
          onReset={onResetILE}
          aiTutorId={aiTutorId}
          isResetting={resettingILEId === aiTutorId}
        />
      </MotiView>
    );
  };

  return (
    <View>
      {items.length > 0 ? (
        <>{items.map(renderResourceCard)}</>
      ) : (
        <View style={{ flex: 1, justifyContent: "center" }}>
          <Placeholder
            title="No activities found"
            subtitle="Visit your Learning Management System to access these activities"
          />
        </View>
      )}
    </View>
  );
}

export default AiTutorCardProgressList;
